import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import {
  Zap,
  Flame,
  Activity,
  Gauge,
  Shuffle,
  TrendingUp,
  TrendingDown,
  Route,
  Maximize2,
  Minimize2,
  X,
  RotateCcw,
  Pencil,
  Lock,
  Save,
} from "lucide-react";
import WeeklyRecapMatrixTable from "./WeeklyRecapMatrixTable.jsx";
import {
  formatMetricValue,
  computeActualVsPlannedPercent,
  getDayDisplayLabel,
  getMetricDisplayLabel,
  getPercentTargetTone,
  getWeeklySimilarityRingColor,
  getWeeklySimilarityTone,
  resolveWeeklyRecapVisibleDays,
  getWeeklyRecapPeakSpeedMetrics,
} from "./weeklyRecapConfig";
import {
  computeMetricTotals,
  computeSimilarityPercent,
  computeWeeklySimilarityForDays,
} from "./weeklyRecapApi";

const DAY_COLOR_PALETTE = ["#38bdf8", "#34d399", "#818cf8", "#a78bfa", "#fbbf24"];

const METRIC_LINE_COLORS = [
  "#38bdf8",
  "#34d399",
  "#818cf8",
  "#f472b6",
  "#fbbf24",
  "#fb7185",
  "#22d3ee",
  "#a78bfa",
];

function PercentToggleSwitch({ checked, onChange, label, isDarkMode }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 select-none">
      <span className="text-[11px] font-semibold uppercase tracking-wider opacity-75">
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 rounded-full transition-colors ${
          checked
            ? "bg-cyan-500"
            : isDarkMode
              ? "bg-gray-700"
              : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}

function ReferenceValueInput({
  metric,
  value,
  isManual,
  disabled,
  isDarkMode,
  onCommit,
  t,
}) {
  const [draft, setDraft] = useState(() =>
    value == null || !Number.isFinite(Number(value)) ? "" : String(value)
  );
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused) return;
    setDraft(value == null || !Number.isFinite(Number(value)) ? "" : String(value));
  }, [value, focused]);

  const decimals = Number.isFinite(Number(metric?.decimals)) ? Number(metric.decimals) : 1;
  const step = decimals <= 0 ? 1 : 1 / 10 ** decimals;

  const commit = async () => {
    const trimmed = String(draft ?? "").trim();
    if (!trimmed) {
      if (isManual) await onCommit({ reset: true });
      return;
    }
    const parsed = Number(trimmed.replace(",", "."));
    if (!Number.isFinite(parsed)) {
      setDraft(value == null ? "" : String(value));
      return;
    }
    if (Number(value) === parsed) return;
    await onCommit({ value: parsed });
  };

  return (
    <div className="mt-1 flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        disabled={disabled}
        value={draft}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          const trimmed = String(next ?? "").trim();
          if (!trimmed) return;
          const parsed = Number(trimmed.replace(",", "."));
          if (Number.isFinite(parsed)) onCommit({ value: parsed });
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            setDraft(value == null ? "" : String(value));
            event.currentTarget.blur();
          }
        }}
        onClick={(event) => event.stopPropagation()}
        className={`w-full min-w-0 rounded-md border px-1.5 py-0.5 text-right text-sm font-semibold tabular-nums outline-none transition ${
          isManual
            ? isDarkMode
              ? "border-amber-500/50 bg-amber-500/10 text-amber-100 focus:border-amber-400"
              : "border-amber-300 bg-amber-50 text-amber-950 focus:border-amber-500"
            : isDarkMode
              ? "border-gray-700 bg-gray-950 text-gray-100 focus:border-gray-500"
              : "border-slate-200 bg-white text-slate-800 focus:border-slate-400"
        } disabled:opacity-60`}
        aria-label={t("weekly_recap.references.edit_value", { defaultValue: "Edit reference" })}
      />
      {isManual ? (
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onCommit({ reset: true });
          }}
          className={`shrink-0 rounded p-0.5 ${
            isDarkMode ? "text-amber-300 hover:bg-amber-500/20" : "text-amber-600 hover:bg-amber-100"
          }`}
          title={t("weekly_recap.references.reset", { defaultValue: "Restore max" })}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function getMetricIcon(key) {
  switch (key) {
    case "global_distance":
      return <Route className="h-4.5 w-4.5 text-sky-500 shrink-0" />;
    case "hsr_distance_m":
      return <Zap className="h-4.5 w-4.5 text-emerald-500 shrink-0" />;
    case "spr_distance_m":
      return <Flame className="h-4.5 w-4.5 text-indigo-500 shrink-0" />;
    case "sprint_count":
      return <Activity className="h-4.5 w-4.5 text-violet-500 shrink-0" />;
    case "sprint_max":
      return <Gauge className="h-4.5 w-4.5 text-amber-500 shrink-0" />;
    case "vmax":
      return <Gauge className="h-4.5 w-4.5 text-orange-500 shrink-0" />;
    case "COD_number":
      return <Shuffle className="h-4.5 w-4.5 text-fuchsia-500 shrink-0" />;
    case "acceleration_max":
      return <TrendingUp className="h-4.5 w-4.5 text-cyan-500 shrink-0" />;
    case "deceleration_max":
      return <TrendingDown className="h-4.5 w-4.5 text-rose-500 shrink-0" />;
    
    // Internal Metrics Icons
    case "hr":
      return <Activity className="h-4.5 w-4.5 text-rose-500 shrink-0" />;
    case "mechanical_fatigue":
      return <TrendingDown className="h-4.5 w-4.5 text-indigo-400 shrink-0" />;
    case "intensity":
      return <Flame className="h-4.5 w-4.5 text-amber-500 shrink-0" />;
    case "mechanical_load":
      return <Gauge className="h-4.5 w-4.5 text-cyan-500 shrink-0" />;

    default:
      return <Activity className="h-4.5 w-4.5 text-gray-500 shrink-0" />;
  }
}

function WeeklyProgressRing({ value, isDarkMode }) {
  const numericValue = toNullableNumber(value);
  const hasValue = numericValue !== null;
  const safe = hasValue ? Math.max(0, Math.min(100, numericValue)) : 0;
  const radius = 50;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (safe / 100) * circumference;

  const color = hasValue ? getWeeklySimilarityRingColor(safe) : "#64748b";

  return (
    <div className="relative flex items-center justify-center select-none filter drop-shadow-md">
      <svg className="h-32 w-32 transform -rotate-90">
        {/* Background Track */}
        <circle
          cx="64"
          cy="64"
          r={radius}
          className={isDarkMode ? "stroke-gray-800" : "stroke-gray-100"}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Foreground Progress */}
        {hasValue ? (
          <circle
            cx="64"
            cy="64"
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            fill="transparent"
            style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        ) : null}
      </svg>
      {/* Central Percentage */}
      <div className="absolute flex flex-col items-center justify-center">
        {hasValue ? (
          <span className="text-3xl font-extrabold tracking-tight tabular-nums">
            {safe.toFixed(0)}<span className="text-sm font-semibold opacity-85">%</span>
          </span>
        ) : (
          <span className="text-xl font-bold opacity-75">N/A</span>
        )}
      </div>
    </div>
  );
}

function SimilarityBadge({ value, isDarkMode }) {
  const numericValue = toNullableNumber(value);
  const hasValue = numericValue !== null;
  const safe = hasValue ? Math.max(0, Math.min(100, numericValue)) : 0;

  const tone = getWeeklySimilarityTone(hasValue ? safe : null, isDarkMode);

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {hasValue ? `${safe.toFixed(0)}%` : "N/A"}
    </span>
  );
}

function toPlayerAvatarSrc(player) {
  if (!player?.profile_picture) return null;
  return `data:image/jpeg;base64,${player.profile_picture}`;
}

function toReadableMatchDate(isoDate) {
  if (!isoDate) return "-";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString();
}

function getTargetMatchOpponent(item) {
  if (!item || typeof item !== "object") return null;

  const directKeys = [
    "opponent",
    "opponent_name",
    "adversaire",
    "adversary",
    "opposition",
    "versus",
    "vs",
  ];

  for (const key of directKeys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  if (typeof item?.label === "string") {
    const label = item.label.trim();
    const lower = label.toLowerCase();
    for (const separator of [" vs ", " v ", " contre ", " - "]) {
      const idx = lower.indexOf(separator);
      if (idx === -1) continue;
      const candidate = label.slice(idx + separator.length).trim();
      if (candidate) return candidate;
    }
  }

  return null;
}

function WeeklyChartTooltip({
  active,
  payload,
  label,
  metric,
  isDarkMode,
  t,
  jDays,
  isPercentMode,
  plannedLabel,
  actualLabel,
}) {
  if (!active || !payload?.length) return null;

  const dayIndex = Math.max(0, jDays.indexOf(label));
  const dayColor = DAY_COLOR_PALETTE[dayIndex % DAY_COLOR_PALETTE.length];

  const planned = payload.find((entry) => entry?.dataKey === "planned");
  const actual = payload.find((entry) => entry?.dataKey === "actual");

  const rows = [
    {
      key: "planned",
      label: plannedLabel || t("weekly_recap.series.planned", { defaultValue: "Planned" }),
      value: planned?.value,
      rawValue: planned?.payload?.rawPlanned,
      opacity: 0.45,
    },
    {
      key: "actual",
      label: actualLabel || t("weekly_recap.series.actual", { defaultValue: "Actual" }),
      value: actual?.value,
      rawValue: actual?.payload?.rawActual,
      opacity: 1,
    },
  ];

  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-xl ${
        isDarkMode
          ? "border-gray-700 bg-gray-950 text-gray-100"
          : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <div className={`mb-1.5 text-xs font-bold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
        {label}
      </div>

      <div className="space-y-1">
        {rows.map((row) => {
          let displayText = "";
          if (isPercentMode) {
            const rawFormatted = formatMetricValue(metric, row.rawValue);
            const pct = toNullableNumber(row.value);
            displayText = pct === null ? "N/A" : `${pct.toFixed(0)}% (${rawFormatted})`;
          } else {
            displayText = formatMetricValue(metric, row.value);
          }

          return (
            <div key={row.key} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor: dayColor,
                    opacity: row.opacity,
                  }}
                />
                <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                  {row.label}
                </span>
              </div>

              <span className={`font-bold tabular-nums ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>
                {displayText}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UnifiedPercentChartTooltip({
  active,
  payload,
  label,
  metrics,
  metricLabelOverrides,
  isDarkMode,
  t,
}) {
  if (!active || !payload?.length) return null;

  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-xl ${
        isDarkMode
          ? "border-gray-700 bg-gray-950 text-gray-100"
          : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <div className={`mb-1.5 text-xs font-bold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
        {label}
      </div>

      <div className="space-y-1">
        {payload.map((entry) => {
          const metric = metrics.find((item) => item.key === entry?.dataKey);
          const pct = toNullableNumber(entry?.value);
          const rawActual = entry?.payload?.[`${entry.dataKey}_raw`];
          const rawFormatted = metric ? formatMetricValue(metric, rawActual) : rawActual;

          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: entry.color || entry.stroke }}
                />
                <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                  {metric ? getMetricDisplayLabel(metric, t, metricLabelOverrides) : entry.dataKey}
                </span>
              </div>

              <span
                className={`font-bold tabular-nums ${
                  pct === null
                    ? isDarkMode
                      ? "text-gray-400"
                      : "text-gray-500"
                    : getPercentTargetTone(pct, isDarkMode)
                }`}
              >
                {pct === null ? "N/A" : `${pct.toFixed(0)}% (${rawFormatted})`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getPercentDotColor(percent) {
  const value = toNullableNumber(percent);
  if (value === null) return "#94a3b8";

  const deviation = Math.abs(value - 100);
  if (deviation <= 15) return "#10b981";
  if (deviation <= 30) return "#f59e0b";
  return "#ef4444";
}

function UnifiedMetricsPercentChart({
  metrics,
  jDays,
  plannedValues,
  actualValues,
  metricLabelOverrides,
  dayLabelOverrides = {},
  isDarkMode,
  t,
}) {
  const chartData = useMemo(() => {
    return jDays.map((day) => {
      const row = { day };

      metrics.forEach((metric) => {
        const rawPlanned = plannedValues?.[metric.key]?.[day];
        const rawActual = actualValues?.[metric.key]?.[day];
        row[metric.key] = computeActualVsPlannedPercent(rawPlanned, rawActual);
        row[`${metric.key}_raw`] = rawActual;
        row[`${metric.key}_planned`] = rawPlanned;
      });

      return row;
    });
  }, [metrics, jDays, plannedValues, actualValues]);

  const yDomain = useMemo(() => {
    const values = chartData.flatMap((row) =>
      metrics
        .map((metric) => toNullableNumber(row[metric.key]))
        .filter((value) => value !== null),
    );

    if (!values.length) return [0, 150];

    const min = Math.min(...values, 100);
    const max = Math.max(...values, 100);
    const padding = Math.max(10, (max - min) * 0.15);

    return [
      Math.max(0, Math.floor(min - padding)),
      Math.ceil(max + padding),
    ];
  }, [chartData, metrics]);

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={isDarkMode ? "#374151" : "#e5e7eb"}
          />
          <XAxis
            dataKey="day"
            stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
            tick={{ fontSize: 12, fontWeight: 600 }}
            tickFormatter={(day) => getDayDisplayLabel(day, dayLabelOverrides)}
          />
          <YAxis
            domain={yDomain}
            stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip
            cursor={{
              stroke: isDarkMode ? "#64748b" : "#94a3b8",
              strokeDasharray: "4 4",
            }}
            content={({ active, payload, label }) => (
              <UnifiedPercentChartTooltip
                active={active}
                payload={payload}
                label={label}
                metrics={metrics}
                metricLabelOverrides={metricLabelOverrides}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}
          />
          <Legend
            verticalAlign="top"
            height={36}
            formatter={(value) => {
              const metric = metrics.find((item) => item.key === value);
              return metric ? getMetricDisplayLabel(metric, t, metricLabelOverrides) : value;
            }}
          />
          <ReferenceLine
            y={100}
            stroke={isDarkMode ? "#64748b" : "#94a3b8"}
            strokeDasharray="6 4"
            label={{
              value: "100%",
              position: "insideTopRight",
              fill: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: 11,
              fontWeight: 700,
            }}
          />

          {metrics.map((metric, index) => {
            const color = METRIC_LINE_COLORS[index % METRIC_LINE_COLORS.length];

            return (
              <Line
                key={metric.key}
                type="monotone"
                dataKey={metric.key}
                name={metric.key}
                stroke={color}
                strokeWidth={2.5}
                connectNulls
                dot={({ cx, cy, payload }) => {
                  const pct = payload?.[metric.key];
                  if (cx === undefined || cy === undefined || pct === null || pct === undefined) {
                    return null;
                  }

                  const dotColor = getPercentDotColor(pct);

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill={dotColor}
                      stroke={isDarkMode ? "#111827" : "#ffffff"}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 6 }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function WeeklyTrendTooltip({ active, payload, label, isDarkMode }) {
  if (!active || !payload?.length) return null;

  const value = toNullableNumber(payload?.[0]?.value);

  return (
    <div
      className={`rounded-lg border px-2.5 py-1.5 text-xs shadow-lg ${
        isDarkMode
          ? "border-gray-700 bg-gray-950 text-gray-100"
          : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <div className="font-bold">{label}</div>
      <div className={isDarkMode ? "text-emerald-300" : "text-emerald-600"}>
        {value === null ? "N/A" : `${value.toFixed(0)}%`}
      </div>
    </div>
  );
}

function WeeklyTrendLineChart({ history, isDarkMode, t }) {
  if (!Array.isArray(history) || history.length === 0) return null;

  return (
    <div className="flex min-w-[240px] shrink-0 flex-col items-center justify-center p-3 select-none self-center lg:self-auto">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wider opacity-75">
        {t("weekly_recap.trend_history_title", { defaultValue: "5-Week Trend" })}
      </h4>

      <div className="h-24 w-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={history} margin={{ top: 8, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke={isDarkMode ? "#374151" : "#e5e7eb"}
            />
            <XAxis
              dataKey="week"
              axisLine={false}
              tickLine={false}
              stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
              tick={{ fontSize: 10, fontWeight: 700 }}
            />
            <YAxis domain={[0, 100]} hide />
            <Tooltip
              cursor={{
                stroke: isDarkMode ? "#64748b" : "#94a3b8",
                strokeDasharray: "4 4",
              }}
              content={({ active, payload, label }) => (
                <WeeklyTrendTooltip
                  active={active}
                  payload={payload}
                  label={label}
                  isDarkMode={isDarkMode}
                />
              )}
            />
            <Line
              type="monotone"
              dataKey="pct"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={{ r: 3, strokeWidth: 2, fill: isDarkMode ? "#111827" : "#ffffff" }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function WeeklyRecapDataCollectionDevBanner({ dataCollection, bundle, isDarkMode, isDev, t }) {
  if (!isDev || !dataCollection) return null;

  const status = dataCollection?.status || "unknown";
  if (status === "ok") return null;

  const issues = Array.isArray(dataCollection?.issues) ? dataCollection.issues : [];

  return (
    <div
      className={`rounded-xl border px-3 py-2 text-xs ${
        isDarkMode
          ? "border-amber-700/60 bg-amber-950/30 text-amber-100"
          : "border-amber-300 bg-amber-50 text-amber-900"
      }`}
    >
      <div className="font-bold uppercase tracking-wide">
        {t("weekly_recap.dev.data_collection_title", {
          defaultValue: "Dev — data collection",
        })}
      </div>
      <div className="mt-1 opacity-90">
        {t("weekly_recap.dev.data_collection_status", {
          defaultValue: "Status: {{status}}",
          status,
        })}
      </div>
      <div className="mt-1 grid gap-0.5 opacity-85">
        <span>
          {t("weekly_recap.dev.dated_simulations", {
            defaultValue: "Dated simulations: {{dated}} / {{total}}",
            dated: dataCollection?.dated_simulation_count ?? 0,
            total: dataCollection?.season_simulation_count ?? 0,
          })}
        </span>
        <span>
          {t("weekly_recap.dev.microcycle_coverage", {
            defaultValue: "Microcycle days with data: {{filled}} / {{total}}",
            filled: dataCollection?.microcycle_days_with_data ?? 0,
            total: dataCollection?.microcycle_days_total ?? 0,
          })}
        </span>
        {dataCollection?.missing_days?.length ? (
          <span>
            {t("weekly_recap.dev.missing_days", {
              defaultValue: "Missing MD+N: {{days}}",
              days: dataCollection.missing_days.join(", "),
            })}
          </span>
        ) : null}
        {issues.length ? (
          <span>
            {t("weekly_recap.dev.issues", {
              defaultValue: "Issues: {{issues}}",
              issues: issues.join(", "),
            })}
          </span>
        ) : null}
        {bundle?.source ? (
          <span className="opacity-70">
            source={bundle.source}
            {bundle?.endpoint ? ` · ${bundle.endpoint}` : ""}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function WeeklyRecapLoadFailureDevBanner({
  bundle,
  selectedTargetMatchDate,
  selectedTargetMatch,
  selectedTargetOpponent,
  isDarkMode,
  isDev,
  t,
}) {
  if (!isDev || !bundle) return null;

  const reasons = [];
  const status = bundle?.status || "unknown";
  const issues = Array.isArray(bundle?.dataCollection?.issues) ? bundle.dataCollection.issues : [];
  const hasNonOkStatus = status !== "ok";
  const hasFallbackSource = bundle?.source === "fallback";

  if (!hasFallbackSource && !hasNonOkStatus && selectedTargetMatch?.player_has_match_data !== false && issues.length === 0) {
    return null;
  }

  if (hasFallbackSource) {
    reasons.push(
      t("weekly_recap.dev.load_reason_fallback", {
        defaultValue: "Bundle loaded from fallback source (API not usable for this request).",
      }),
    );
  }

  if (hasNonOkStatus) {
    reasons.push(
      t("weekly_recap.dev.load_reason_status", {
        defaultValue: "Recap status returned by API: {{status}}.",
        status,
      }),
    );
  }

  if (selectedTargetMatchDate && selectedTargetMatch?.player_has_match_data === false) {
    reasons.push(
      t("weekly_recap.dev.load_reason_missing_player_match", {
        defaultValue: "No player simulation found for selected target match {{date}}.",
        date: toReadableMatchDate(selectedTargetMatchDate),
      }),
    );
  }

  if (issues.length > 0) {
    reasons.push(
      t("weekly_recap.dev.load_reason_issues", {
        defaultValue: "Data collection issues: {{issues}}.",
        issues: issues.join(", "),
      }),
    );
  }

  const technicalReason = bundle?.loadError || bundle?.sourceDetails;

  return (
    <div
      className={`rounded-xl border px-3 py-2 text-xs ${
        isDarkMode
          ? "border-rose-700/60 bg-rose-950/30 text-rose-100"
          : "border-rose-300 bg-rose-50 text-rose-900"
      }`}
    >
      <div className="font-bold uppercase tracking-wide">
        {t("weekly_recap.dev.recap_load_title", {
          defaultValue: "Dev - recap load diagnostics",
        })}
      </div>
      <div className="mt-1 opacity-90">
        {t("weekly_recap.dev.recap_load_context", {
          defaultValue: "Target match: {{date}} {{opponent}}",
          date: selectedTargetMatchDate ? toReadableMatchDate(selectedTargetMatchDate) : "-",
          opponent: selectedTargetOpponent
            ? t("weekly_recap.target_match.selected_against", {
              defaultValue: "(vs {{opponent}})",
              opponent: selectedTargetOpponent,
            })
            : "",
        })}
      </div>
      <div className="mt-1 grid gap-0.5 opacity-85">
        {reasons.map((reason, idx) => (
          <span key={`weekly-debug-reason-${idx}`}>- {reason}</span>
        ))}
        {technicalReason ? (
          <span>
            {t("weekly_recap.dev.recap_load_technical", {
              defaultValue: "Technical reason: {{reason}}",
              reason: technicalReason,
            })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function WeeklyRecapPlayerPanel({
  player,
  metrics,
  similarityMetrics,
  weeklySimilarityValue,
  bundle,
  isDarkMode,
  onOpenPlanning,
  planningLoading,
  globalStatusMessage,
  isInternalMode,
  setIsInternalMode,
  calculateSimilarityFromDay = "MD+1",
  chartDisplayMode = "percent",
  metricLabelOverrides = {},
  dayLabelOverrides = {},
  setChartDisplayMode,
  onTargetMatchChange,
  targetMatchLoading,
  targetMatchDate = null,
  availableTargetMatches: availableTargetMatchesProp = [],
  targetMatchOpponent = null,
  isDev = false,
  onSaveReferences,
  savingReferences = false,
}) {
  const { t } = useTranslation();
  const isPercentMode = chartDisplayMode === "percent";
  const [selectedReferenceMetricKey, setSelectedReferenceMetricKey] = useState(null);
  const [actualTablePercentMode, setActualTablePercentMode] = useState(false);
  const [isMatrixFullscreen, setIsMatrixFullscreen] = useState(false);
  const [isEditingReferences, setIsEditingReferences] = useState(false);
  const [draftReferenceValues, setDraftReferenceValues] = useState({});
  const [pendingResetKeys, setPendingResetKeys] = useState(() => new Set());

  const jDays = bundle?.jDays || [];
  const matchDayLabel = bundle?.nextMatchDay || bundle?.next_match_day || null;
  const referenceValues = bundle?.referenceValues || {};
  const computedReferenceValues = bundle?.computedReferenceValues || {};
  const referenceOverrides = bundle?.referenceOverrides || {};
  const plannedValues = bundle?.plannedValues || {};
  const actualValues = bundle?.actualValues || {};
  const similarityByMetric = bundle?.similarityByMetric || {};
  const bundleTargetMatchDate = bundle?.targetMatchDate || null;
  const bundleTargetMatches = Array.isArray(bundle?.availableTargetMatches)
    ? bundle.availableTargetMatches
    : [];
  const availableTargetMatches = Array.isArray(availableTargetMatchesProp) && availableTargetMatchesProp.length > 0
    ? availableTargetMatchesProp
    : bundleTargetMatches;
  const referenceMetricSources = bundle?.referenceMetricSources || {};
  const referenceScope = bundle?.referenceScope || null;
  const referenceMatchDate = bundle?.referenceMatchDate || null;
  const similarityHistory = Array.isArray(bundle?.similarityHistory) ? bundle.similarityHistory : [];
  const dataCollection = bundle?.dataCollection || null;
  const selectedTargetMatchDate = targetMatchDate || bundleTargetMatchDate || "";

  const selectedTargetMatch = useMemo(
    () => availableTargetMatches.find((item) => item?.date === selectedTargetMatchDate) || null,
    [availableTargetMatches, selectedTargetMatchDate],
  );

  const selectedTargetMatchFromBundle = useMemo(
    () => bundleTargetMatches.find((item) => item?.date === selectedTargetMatchDate) || null,
    [bundleTargetMatches, selectedTargetMatchDate],
  );

  const selectedTargetOpponent = targetMatchOpponent
    || bundle?.targetMatchOpponent
    || getTargetMatchOpponent(selectedTargetMatchFromBundle)
    || getTargetMatchOpponent(selectedTargetMatch)
    || null;

  useEffect(() => {
    setSelectedReferenceMetricKey(null);
    setActualTablePercentMode(false);
    setIsMatrixFullscreen(false);
    setIsEditingReferences(false);
    setDraftReferenceValues({});
    setPendingResetKeys(new Set());
  }, [player?.id]);

  useEffect(() => {
    if (!isInternalMode) return;
    setIsMatrixFullscreen(false);
  }, [isInternalMode]);

  useEffect(() => {
    if (!isMatrixFullscreen) return undefined;

    const onEsc = (event) => {
      if (event.key === "Escape") {
        setIsMatrixFullscreen(false);
      }
    };

    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isMatrixFullscreen]);

  const peakSpeedMetrics = useMemo(() => getWeeklyRecapPeakSpeedMetrics(), []);

  const referenceCardMetrics = useMemo(() => {
    if (isInternalMode) return metrics;
    const seen = new Set((metrics || []).map((metric) => metric.key));
    return [
      ...(metrics || []),
      ...peakSpeedMetrics.filter((metric) => !seen.has(metric.key)),
    ];
  }, [isInternalMode, metrics, peakSpeedMetrics]);

  const {
    visibleDays,
    weeklyPercentStartDay,
    weeklyPercentDays,
  } = useMemo(
    () => resolveWeeklyRecapVisibleDays({
      jDays,
      actualValues,
      metrics,
      matchDayLabel: matchDayLabel || "MD",
    }),
    [jDays, actualValues, metrics, matchDayLabel],
  );

  const filteredSimilarityByMetric = useMemo(() => {
    const plannedTotalsForDays = computeMetricTotals(plannedValues, metrics, visibleDays);
    const actualTotalsForDays = computeMetricTotals(actualValues, metrics, visibleDays);
    return Object.fromEntries(
      metrics.map((metric) => [
        metric.key,
        computeSimilarityPercent(
          plannedTotalsForDays?.[metric.key],
          actualTotalsForDays?.[metric.key],
        ),
      ]),
    );
  }, [plannedValues, actualValues, metrics, visibleDays]);

  const computedExternalWeeklySimilarity = useMemo(
    () => computeWeeklySimilarityForDays({
      plannedMatrix: plannedValues,
      actualMatrix: actualValues,
      metrics: similarityMetrics?.length ? similarityMetrics : metrics,
      jDays: weeklyPercentDays,
    }),
    [plannedValues, actualValues, similarityMetrics, metrics, weeklyPercentDays],
  );

  const weeklySimilarity = toNullableNumber(
    (!isInternalMode && computedExternalWeeklySimilarity !== null)
      ? computedExternalWeeklySimilarity
      : (weeklySimilarityValue ?? bundle?.externalWeeklySimilarity ?? bundle?.weeklySimilarity),
  );

  const plannedTotals = computeMetricTotals(plannedValues, metrics, visibleDays);
  const actualTotals = computeMetricTotals(actualValues, metrics, visibleDays);

  const objectiveBanner = !isInternalMode ? (
    <>
      <span className="font-bold uppercase tracking-wider opacity-80">
        {t("weekly_recap.objective.label", { defaultValue: "Objective" })}
        :
      </span>
      {" "}
      <span className="font-semibold">
        {toReadableMatchDate(selectedTargetMatchDate)}
        {selectedTargetOpponent
          ? ` ${t("weekly_recap.target_match.selected_against", {
            defaultValue: "(vs {{opponent}})",
            opponent: selectedTargetOpponent,
          })}`
          : ""}
      </span>
    </>
  ) : null;

  const weeklyPercentIndicator = !isInternalMode && weeklyPercentStartDay ? (
    t("weekly_recap.week_percent_from", {
      defaultValue: "Weekly percentage calculated from {{day}}",
      day: getDayDisplayLabel(weeklyPercentStartDay, dayLabelOverrides),
    })
  ) : null;

  const vmaMaxByDay = bundle?.vmaMaxByDay || {};

  const peakActualMatrix = useMemo(() => {
    const vmaxByDay = Object.fromEntries(
      visibleDays.map((day) => [
        day,
        toNullableNumber(actualValues?.vmax?.[day] ?? vmaMaxByDay?.[day]),
      ]),
    );
    const sprintByDay = Object.fromEntries(
      visibleDays.map((day) => [
        day,
        toNullableNumber(actualValues?.sprint_max?.[day]),
      ]),
    );
    return {
      vmax: vmaxByDay,
      sprint_max: sprintByDay,
    };
  }, [actualValues, vmaMaxByDay, visibleDays]);

  const peakReferenceMatrix = useMemo(() => (
    Object.fromEntries(
      peakSpeedMetrics.map((metric) => [
        metric.key,
        Object.fromEntries(
          visibleDays.map((day) => [
            day,
            toNullableNumber(referenceValues?.[metric.key]),
          ]),
        ),
      ]),
    )
  ), [peakSpeedMetrics, referenceValues, visibleDays]);

  const avatarSrc = toPlayerAvatarSrc(player);

  const initials = `${player?.first_name?.[0] || "P"}${player?.last_name?.[0] || ""}`.toUpperCase();

  const playerName =
    `${player?.first_name || ""} ${player?.last_name || ""}`.trim() ||
    t("common.player", { defaultValue: "Player" });

  const trendHistory = useMemo(() => {
    const entries = [...similarityHistory]
      .filter((row) => row && typeof row === "object")
      .sort((a, b) => String(a?.calculated_at || "").localeCompare(String(b?.calculated_at || "")))
      .slice(-5)
      .map((row, index) => ({
        week: toReadableMatchDate(row?.target_match_date || row?.today) || `W-${4 - index}`,
        pct: toNullableNumber(row?.weekly_similarity),
      }))
      .filter((row) => row.pct !== null)
      .map((row) => ({
        ...row,
        pct: Math.max(0, Math.min(100, row.pct)),
      }));

    if (entries.length > 0) return entries;

    if (weeklySimilarity === null) return [];
    return [{ week: t("weekly_recap.trend.current", { defaultValue: "Now" }), pct: weeklySimilarity }];
  }, [similarityHistory, weeklySimilarity, t]);

  const getDisplayReferenceValue = (metricKey) => {
    if (pendingResetKeys.has(metricKey)) {
      return computedReferenceValues?.[metricKey] ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(draftReferenceValues, metricKey)) {
      return draftReferenceValues[metricKey];
    }
    return referenceValues?.[metricKey];
  };

  const isSavedManualReference = (metricKey) => (
    Object.prototype.hasOwnProperty.call(referenceOverrides || {}, metricKey)
    || referenceMetricSources?.[metricKey]?.kind === "manual"
  );

  const isManualReference = (metricKey) => {
    if (pendingResetKeys.has(metricKey)) return false;
    if (Object.prototype.hasOwnProperty.call(draftReferenceValues, metricKey)) return true;
    return isSavedManualReference(metricKey);
  };

  const formatReferenceScope = (scope) => {
    switch (scope) {
      case "target_match":
        return t("weekly_recap.references.scope_target_match", {
          defaultValue: "Max observed on matches",
        });
      case "season_real_sessions":
        return t("weekly_recap.references.scope_season", {
          defaultValue: "Season real sessions",
        });
      case "no_completed_target_match":
        return t("weekly_recap.references.scope_none", {
          defaultValue: "No match source",
        });
      default:
        return scope || "-";
    }
  };

  const getReferenceMatchDate = (metricKey) => (
    referenceMetricSources?.[metricKey]?.match_date || referenceMatchDate || null
  );

  const getAutoMaxDeltaLabel = (metric) => {
    const current = toNullableNumber(getDisplayReferenceValue(metric.key));
    const autoMax = toNullableNumber(computedReferenceValues?.[metric.key]);
    if (current === null || autoMax === null || current === autoMax) return null;
    if (autoMax === 0) {
      return t("weekly_recap.references.vs_auto_max", {
        defaultValue: "vs max {{value}}",
        value: formatMetricValue(metric, autoMax),
      });
    }
    const pct = ((current - autoMax) / Math.abs(autoMax)) * 100;
    const signed = `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`;
    return t("weekly_recap.references.delta_vs_max", {
      defaultValue: "{{delta}} vs max",
      delta: signed,
    });
  };

  const renderReferenceProvenance = (metric, compact = false) => {
    const isManual = isManualReference(metric.key);
    const sourceDate = toReadableMatchDate(getReferenceMatchDate(metric.key));
    const autoMaxLabel = formatMetricValue(metric, computedReferenceValues?.[metric.key]);
    const deltaLabel = isManual ? getAutoMaxDeltaLabel(metric) : null;

    return (
      <div className={`${compact ? "mt-0.5 text-[9px] leading-tight" : "mt-1 text-[10px] leading-snug"} ${
        isDarkMode ? "text-gray-400" : "text-gray-500"
      }`}>
        <div className="truncate">
          {isManual
            ? t("weekly_recap.references.source_manual", { defaultValue: "Manual equivalent" })
            : t("weekly_recap.references.source_auto", {
              defaultValue: "Max · {{date}}",
              date: sourceDate,
            })}
        </div>
        {isManual ? (
          <div className={isDarkMode ? "text-amber-300" : "text-amber-700"}>
            {t("weekly_recap.references.vs_auto_max", {
              defaultValue: "vs max {{value}}",
              value: autoMaxLabel,
            })}
            {deltaLabel ? ` · ${deltaLabel}` : ""}
          </div>
        ) : null}
      </div>
    );
  };

  const hasManualOverrides = referenceCardMetrics.some((metric) => isManualReference(metric.key));

  const canEditReferences = typeof onSaveReferences === "function";
  const showReferenceEditor = canEditReferences && isEditingReferences;
  const hasDirtyReferenceDrafts =
    pendingResetKeys.size > 0
    || Object.keys(draftReferenceValues).length > 0;
  const selectedReferenceMetric = referenceCardMetrics.find((item) => item.key === selectedReferenceMetricKey) || null;
  const selectedReferenceDelta = selectedReferenceMetric
    ? getAutoMaxDeltaLabel(selectedReferenceMetric)
    : null;

  const discardReferenceDrafts = () => {
    setDraftReferenceValues({});
    setPendingResetKeys(new Set());
    setIsEditingReferences(false);
  };

  const startEditingReferences = () => {
    setDraftReferenceValues({});
    setPendingResetKeys(new Set());
    setIsEditingReferences(true);
  };

  const commitReference = (metricKey, { value, reset } = {}) => {
    if (!canEditReferences || !showReferenceEditor) return;
    setPendingResetKeys((prev) => {
      const next = new Set(prev);
      if (reset) next.add(metricKey);
      else next.delete(metricKey);
      return next;
    });
    setDraftReferenceValues((prev) => {
      const next = { ...prev };
      if (reset) {
        delete next[metricKey];
      } else {
        next[metricKey] = value;
      }
      return next;
    });
  };

  const handleResetAllReferences = () => {
    if (!canEditReferences) return;
    const keysToReset = referenceCardMetrics
      .map((metric) => metric.key)
      .filter((key) => (
        isSavedManualReference(key)
        || Object.prototype.hasOwnProperty.call(draftReferenceValues, key)
        || pendingResetKeys.has(key)
      ));
    if (!keysToReset.length) return;
    const confirmed = window.confirm(
      t("weekly_recap.references.reset_all_confirm", {
        defaultValue: "Restore all automatic match-max references for this player?",
      }),
    );
    if (!confirmed) return;
    setPendingResetKeys(new Set(keysToReset));
    setDraftReferenceValues({});
  };

  const handleSaveReferenceDrafts = async () => {
    if (!canEditReferences) return;

    const resetMetrics = Array.from(pendingResetKeys);
    const referenceMetrics = {};
    Object.entries(draftReferenceValues).forEach(([key, value]) => {
      if (pendingResetKeys.has(key)) return;
      const numeric = toNullableNumber(value);
      const current = toNullableNumber(referenceValues?.[key]);
      if (numeric === current) return;
      if (numeric === null) {
        resetMetrics.push(key);
        return;
      }
      referenceMetrics[key] = numeric;
    });

    if (!Object.keys(referenceMetrics).length && !resetMetrics.length) {
      discardReferenceDrafts();
      return;
    }

    try {
      await onSaveReferences({ referenceMetrics, resetMetrics });
      discardReferenceDrafts();
    } catch {
      // Status is already surfaced by WeeklyRecapTab.
    }
  };

  const renderReferenceEditor = (metric, compact = false) => {
    if (!showReferenceEditor) {
      return (
        <span className={compact
          ? "font-bold tabular-nums text-cyan-600 dark:text-cyan-400"
          : "mt-1 block text-sm font-extrabold tabular-nums"}
        >
          {formatMetricValue(metric, getDisplayReferenceValue(metric.key))}
        </span>
      );
    }

    return (
      <ReferenceValueInput
        metric={metric}
        value={getDisplayReferenceValue(metric.key)}
        isManual={isManualReference(metric.key)}
        disabled={savingReferences}
        isDarkMode={isDarkMode}
        onCommit={(payload) => commitReference(metric.key, payload)}
        t={t}
      />
    );
  };

  const renderMatrixFullscreenButton = () => (
    <button
      type="button"
      onClick={() => setIsMatrixFullscreen((prev) => !prev)}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
        isDarkMode
          ? "border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800 hover:text-white"
          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      }`}
      title={isMatrixFullscreen
        ? t("weekly_recap.tables.exit_fullscreen", { defaultValue: "Exit fullscreen tables" })
        : t("weekly_recap.tables.open_fullscreen", { defaultValue: "Open tables in fullscreen" })}
      aria-label={isMatrixFullscreen
        ? t("weekly_recap.tables.exit_fullscreen", { defaultValue: "Exit fullscreen tables" })
        : t("weekly_recap.tables.open_fullscreen", { defaultValue: "Open tables in fullscreen" })}
    >
      {isMatrixFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
    </button>
  );

  const plannedTableNode = (
    <WeeklyRecapMatrixTable
      title={t("weekly_recap.tables.planned_title", { defaultValue: "Planned values" })}
      subtitle={t("weekly_recap.tables.planned_subtitle", {
        defaultValue: "Values computed from configured percentages for each MD+N",
      })}
      metrics={metrics}
      jDays={visibleDays}
      matchDayLabel={matchDayLabel}
      matrix={plannedValues}
      totals={plannedTotals}
      isDarkMode={isDarkMode}
      metricLabelOverrides={metricLabelOverrides}
      dayLabelOverrides={dayLabelOverrides}
      objectiveBanner={objectiveBanner}
      headerActions={renderMatrixFullscreenButton()}
    />
  );

  const actualTableNode = (
    <WeeklyRecapMatrixTable
      title={t("weekly_recap.tables.actual_title", { defaultValue: "Actual values" })}
      subtitle={
        actualTablePercentMode
          ? t("weekly_recap.tables.actual_percent_subtitle", {
            defaultValue: "Actual load as % of planned values (100% = on target)",
          })
          : t("weekly_recap.tables.actual_subtitle", {
            defaultValue: "Measured values by MD+N session compared to planning",
          })
      }
      metrics={metrics}
      jDays={visibleDays}
      matchDayLabel={matchDayLabel}
      matrix={actualValues}
      totals={actualTotals}
      isDarkMode={isDarkMode}
      isPercentMode={actualTablePercentMode}
      percentReferenceMatrix={plannedValues}
      metricLabelOverrides={metricLabelOverrides}
      dayLabelOverrides={dayLabelOverrides}
      objectiveBanner={objectiveBanner}
      weeklyPercentIndicator={weeklyPercentIndicator}
      extraHeader={t("weekly_recap.columns.similarity", { defaultValue: "Similarity" })}
      renderExtraCell={({ metricKey }) => (
        <SimilarityBadge value={filteredSimilarityByMetric?.[metricKey]} isDarkMode={isDarkMode} />
      )}
      headerActions={(
        <div className="flex items-center gap-2">
          <PercentToggleSwitch
            checked={actualTablePercentMode}
            onChange={setActualTablePercentMode}
            label={t("weekly_recap.controls.percentages", { defaultValue: "Percentage (%)" })}
            isDarkMode={isDarkMode}
          />
          {renderMatrixFullscreenButton()}
        </div>
      )}
    />
  );

  const peakTableNode = !isInternalMode && peakSpeedMetrics.length ? (
    <WeeklyRecapMatrixTable
      title={t("weekly_recap.tables.peak_title", { defaultValue: "Vmax and sprint max" })}
      subtitle={
        actualTablePercentMode
          ? t("weekly_recap.tables.peak_percent_subtitle", {
            defaultValue: "Actual value as % of the reference max (100% = max reached)",
          })
          : t("weekly_recap.tables.peak_subtitle", {
            defaultValue: "Daily comparison against the reference max — no totals, not included in weekly percentage",
          })
      }
      metrics={peakSpeedMetrics}
      jDays={visibleDays}
      matchDayLabel={matchDayLabel}
      matrix={peakActualMatrix}
      isDarkMode={isDarkMode}
      isPercentMode={actualTablePercentMode}
      percentReferenceMatrix={peakReferenceMatrix}
      metricLabelOverrides={metricLabelOverrides}
      dayLabelOverrides={dayLabelOverrides}
      objectiveBanner={objectiveBanner}
      hideTotals
      extraHeader={t("weekly_recap.columns.reference_max", { defaultValue: "Reference max" })}
      renderExtraCell={({ metric }) => (
        <span className="block text-center text-xs font-bold tabular-nums text-cyan-600 dark:text-cyan-400 sm:text-sm">
          {formatMetricValue(metric, referenceValues?.[metric.key])}
        </span>
      )}
      headerActions={(
        <PercentToggleSwitch
          checked={actualTablePercentMode}
          onChange={setActualTablePercentMode}
          label={t("weekly_recap.controls.percentages", { defaultValue: "Percentage (%)" })}
          isDarkMode={isDarkMode}
        />
      )}
    />
  ) : null;

  return (
    <div className={`space-y-6 px-1 pb-4 transition-opacity ${targetMatchLoading ? "pointer-events-none opacity-55" : ""}`}>
      {/* 1. Header: Elegant, Unboxed, Floating display of the selected player */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-2 border-b border-dashed border-gray-200/50 dark:border-gray-800/50">
        <div className="flex items-center gap-4">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={playerName}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-cyan-500/50 shadow-md"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 font-bold text-white text-lg shadow-md">
              {initials}
            </div>
          )}
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">
              {playerName}
            </h2>
            <p className="flex items-center gap-2 mt-1 text-sm font-semibold opacity-70">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
              {player?.position || t("common.na", { defaultValue: "N/A" })}
            </p>
          </div>
        </div>

        {globalStatusMessage ? (
          <span
            className={`rounded-lg px-2.5 py-1 text-[11px] font-medium ${
              globalStatusMessage.tone === "success"
                ? isDarkMode
                  ? "bg-emerald-900/30 text-emerald-300"
                  : "bg-emerald-100 text-emerald-700"
                : isDarkMode
                  ? "bg-amber-900/30 text-amber-300"
                  : "bg-amber-100 text-amber-700"
            }`}
          >
            {globalStatusMessage.text}
          </span>
        ) : null}
      </div>

      <WeeklyRecapDataCollectionDevBanner
        dataCollection={dataCollection}
        bundle={bundle}
        isDarkMode={isDarkMode}
        isDev={isDev}
        t={t}
      />

      <WeeklyRecapLoadFailureDevBanner
        bundle={bundle}
        selectedTargetMatchDate={selectedTargetMatchDate || null}
        selectedTargetMatch={selectedTargetMatchFromBundle || selectedTargetMatch}
        selectedTargetOpponent={selectedTargetOpponent}
        isDarkMode={isDarkMode}
        isDev={isDev}
        t={t}
      />

      {/* 2. Side-by-side row: Centered circular progress (unboxed, plus en avant), 5-week history trend sparklines, & boxed reference metrics */}
      <div className="flex flex-col lg:flex-row items-stretch gap-6">
        {/* Weekly similarity */}
        <div className="flex flex-col items-center justify-center p-3 text-center min-w-[200px] select-none shrink-0 self-center lg:self-auto">
          <h4 className="text-xs font-bold uppercase tracking-wider mb-4 opacity-75">
            {t("weekly_recap.week_similarity_title", { defaultValue: "Weekly similarity" })}
          </h4>
          <WeeklyProgressRing value={weeklySimilarity} isDarkMode={isDarkMode} />
        </div>

        {!isInternalMode ? (
          <WeeklyTrendLineChart
            history={trendHistory}
            isDarkMode={isDarkMode}
            t={t}
          />
        ) : null}

        {/* Reference Metrics Block: Boxed/Bordered, containing the metric items with custom icons */}
        <div className={`flex-1 rounded-2xl border p-4 flex flex-col justify-center ${
          isDarkMode
            ? `border-gray-800 bg-gray-900/40 ${isEditingReferences ? "ring-1 ring-amber-400/40" : ""}`
            : `border-gray-200 bg-white ${isEditingReferences ? "ring-1 ring-amber-300/70" : ""}`
        }`}>
          <div className="mb-1 flex items-start justify-between gap-3">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider opacity-60">
              {!isEditingReferences ? <Lock className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              {t("weekly_recap.references.title", { defaultValue: "Match-average references" })}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1">
              {showReferenceEditor && hasManualOverrides ? (
                <button
                  type="button"
                  disabled={savingReferences}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleResetAllReferences}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    isDarkMode
                      ? "text-amber-300 hover:bg-amber-500/10"
                      : "text-amber-700 hover:bg-amber-50"
                  } disabled:opacity-50`}
                >
                  <RotateCcw className="h-3 w-3" />
                  {t("weekly_recap.references.reset_all", { defaultValue: "Restore all" })}
                </button>
              ) : null}
              {canEditReferences ? (
                isEditingReferences ? (
                  <>
                    <button
                      type="button"
                      disabled={savingReferences}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={discardReferenceDrafts}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${
                        isDarkMode
                          ? "border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      } disabled:opacity-50`}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </button>
                    <button
                      type="button"
                      disabled={savingReferences}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={handleSaveReferenceDrafts}
                      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold ${
                        hasDirtyReferenceDrafts
                          ? isDarkMode
                            ? "bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30"
                            : "bg-cyan-50 text-cyan-800 hover:bg-cyan-100"
                          : isDarkMode
                            ? "bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                            : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                      } disabled:opacity-50`}
                    >
                      <Save className="h-3.5 w-3.5" />
                      {t("weekly_recap.references.save", { defaultValue: "Save" })}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEditingReferences}
                    className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${
                      isDarkMode
                        ? "border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800"
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {t("weekly_recap.references.edit", { defaultValue: "Edit references" })}
                  </button>
                )
              ) : null}
            </div>
          </div>
          <p className={`mb-3 text-[11px] leading-snug ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {savingReferences
              ? t("weekly_recap.references.saving", { defaultValue: "Saving..." })
              : isEditingReferences
                ? t("weekly_recap.references.hint", {
                  defaultValue: "These values are 100% for planning. Edit them, then save — they will be reused for this player.",
                })
                : t("weekly_recap.references.locked_hint", {
                  defaultValue: "References are locked. Press edit to set a reserve equivalent or custom max.",
                })}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {referenceCardMetrics.map((metric) => {
              const isManual = isManualReference(metric.key);
              const isSelected = selectedReferenceMetricKey === metric.key;
              return (
                <div
                  key={`${player?.id}-${metric.key}`}
                  onClick={() =>
                    setSelectedReferenceMetricKey((current) => (current === metric.key ? null : metric.key))
                  }
                  className={`cursor-pointer rounded-xl border p-3 flex items-start gap-2.5 transition-all duration-200 ${
                    isDarkMode
                      ? `border-gray-800 bg-gray-900/60 hover:bg-gray-800/50 ${isSelected ? "ring-1 ring-cyan-500/40" : ""} ${isManual ? "border-amber-500/30" : ""}`
                      : `border-gray-100 bg-gray-50/50 hover:bg-gray-100/70 ${isSelected ? "ring-1 ring-cyan-400/50" : ""} ${isManual ? "border-amber-200" : ""}`
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {getMetricIcon(metric.key)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className={`truncate text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {getMetricDisplayLabel(metric, t, metricLabelOverrides)}
                      </div>
                      {isManual ? (
                        <span className={`shrink-0 rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide ${
                          isDarkMode ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-700"
                        }`}>
                          {t("weekly_recap.references.manual", { defaultValue: "Manual" })}
                        </span>
                      ) : (
                        <span className={`shrink-0 rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide ${
                          isDarkMode ? "bg-slate-700/70 text-slate-200" : "bg-slate-100 text-slate-600"
                        }`}>
                          {t("weekly_recap.references.auto_max", { defaultValue: "Max" })}
                        </span>
                      )}
                    </div>
                    {renderReferenceEditor(metric)}
                    {renderReferenceProvenance(metric)}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedReferenceMetricKey ? (
            <div
              className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                isDarkMode ? "border-gray-700 bg-gray-900/70 text-gray-200" : "border-gray-200 bg-gray-50 text-gray-700"
              }`}
            >
              <div className="font-semibold">
                {t("weekly_recap.references.detail_metric", {
                  defaultValue: "Reference source for {{metric}}",
                  metric: getMetricDisplayLabel(
                    selectedReferenceMetric,
                    t,
                    metricLabelOverrides,
                  ),
                })}
              </div>
              <div className="mt-1 space-y-0.5">
                <div>
                  {t("weekly_recap.references.used_value", {
                    defaultValue: "Used value: {{value}}",
                    value: formatMetricValue(
                      selectedReferenceMetric,
                      getDisplayReferenceValue(selectedReferenceMetricKey),
                    ),
                  })}
                </div>
                <div>
                  {t("weekly_recap.references.auto_max_value", {
                    defaultValue: "Max: {{value}}",
                    value: formatMetricValue(
                      selectedReferenceMetric,
                      computedReferenceValues?.[selectedReferenceMetricKey],
                    ),
                  })}
                  {selectedReferenceDelta ? ` · ${selectedReferenceDelta}` : ""}
                </div>
                {isManualReference(selectedReferenceMetricKey) ? (
                  <div>
                    {t("weekly_recap.references.detail_manual", {
                      defaultValue: "Manually entered reference (custom equivalent).",
                    })}
                  </div>
                ) : null}
                <div>
                  {t("weekly_recap.references.auto_max_match", {
                    defaultValue: "Max from {{date}}",
                    date: toReadableMatchDate(
                      getReferenceMatchDate(selectedReferenceMetricKey),
                    ),
                  })}
                </div>
                <div>
                  {t("weekly_recap.references.detail_target_match", {
                    defaultValue: "Target match: {{date}} {{opponent}}",
                    date: toReadableMatchDate(targetMatchDate),
                    opponent: selectedTargetOpponent
                      ? t("weekly_recap.target_match.selected_against", {
                        defaultValue: "(vs {{opponent}})",
                        opponent: selectedTargetOpponent,
                      })
                      : "",
                  })}
                </div>
                <div>
                  {t("weekly_recap.references.detail_scope", {
                    defaultValue: "Reference scope: {{scope}}",
                    scope: formatReferenceScope(referenceScope),
                  })}
                </div>
              </div>
              {showReferenceEditor && isManualReference(selectedReferenceMetricKey) ? (
                <button
                  type="button"
                  disabled={savingReferences}
                  onClick={(event) => {
                    event.stopPropagation();
                    commitReference(selectedReferenceMetricKey, { reset: true });
                  }}
                  className={`mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold ${
                    isDarkMode
                      ? "bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                      : "bg-amber-50 text-amber-800 hover:bg-amber-100"
                  }`}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {t("weekly_recap.references.reset", { defaultValue: "Restore max" })}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
            isDarkMode
              ? "border-gray-800 bg-gray-900/40"
              : "border-gray-200 bg-white"
          }`}
        >
          <span className="text-xs font-bold uppercase tracking-wider opacity-75">
            {t("weekly_recap.controls.data_type", { defaultValue: "Data type" })}
          </span>

          <div className="inline-flex rounded-lg bg-gray-200 p-0.5 dark:bg-gray-800/80">
            <button
              type="button"
              onClick={() => setIsInternalMode(false)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                !isInternalMode
                  ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {t("weekly_recap.controls.external", { defaultValue: "Physical GPS" })}
            </button>

            <button
              type="button"
              onClick={() => setIsInternalMode(true)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                isInternalMode
                  ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {t("weekly_recap.controls.internal", { defaultValue: "Internal load" })}
            </button>
          </div>
        </div>

        {isInternalMode ? (
          <div className="grid grid-cols-1 gap-4">
            <WeeklyRecapMatrixTable
              title={t("weekly_recap.tables.internal_title", {
                defaultValue: "Internal load overview",
              })}
              subtitle={t("weekly_recap.tables.internal_subtitle", {
                defaultValue: "Internal load values from the player overview data, compared with player references.",
              })}
              metrics={metrics}
              jDays={jDays}
              matchDayLabel={matchDayLabel}
              matrix={actualValues}
              totals={actualTotals}
              isDarkMode={isDarkMode}
              metricLabelOverrides={metricLabelOverrides}
              dayLabelOverrides={dayLabelOverrides}
              extraHeader={t("weekly_recap.columns.reference", { defaultValue: "Reference" })}
              renderExtraCell={({ metric }) => (
                <div className="text-right" onClick={(event) => event.stopPropagation()}>
                  {renderReferenceEditor(metric, true)}
                  {renderReferenceProvenance(metric, true)}
                </div>
              )}
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
              {plannedTableNode}
              {actualTableNode}
            </div>
            {peakTableNode ? <div className="mt-4">{peakTableNode}</div> : null}
          </>
        )}
      </div>

      {!isInternalMode && isMatrixFullscreen ? (
        <div className="fixed inset-0 z-[70] bg-black/75 p-2 backdrop-blur-sm sm:p-3">
          <div
            className={`mx-auto flex h-full w-full max-w-[1880px] flex-col overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode ? "border-gray-700 bg-gray-950 text-gray-100" : "border-gray-200 bg-white text-gray-900"
            }`}
          >
            <div className={`flex items-center justify-between border-b px-4 py-3 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide">
                  {t("weekly_recap.tables.fullscreen_title", { defaultValue: "Planned and actual tables" })}
                </h3>
                <p className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("weekly_recap.tables.fullscreen_subtitle", {
                    defaultValue: "Fullscreen side-by-side view to inspect all MD+N values",
                  })}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsMatrixFullscreen(false)}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800 hover:text-white"
                    : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
                aria-label={t("weekly_recap.tables.exit_fullscreen", { defaultValue: "Exit fullscreen tables" })}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {plannedTableNode}
                {actualTableNode}
              </div>
              {peakTableNode ? <div className="mt-4">{peakTableNode}</div> : null}
            </div>
          </div>
        </div>
      ) : null}

      {!isInternalMode ? (
        <div className={`rounded-2xl ring-1 p-4 ${isDarkMode ? "bg-gray-900/70 ring-white/10" : "bg-white ring-black/10"}`}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide">
                {isPercentMode
                  ? t("weekly_recap.charts.unified_title", {
                    defaultValue: "Load vs planning (%)",
                  })
                  : t("weekly_recap.charts.title", { defaultValue: "Comparison charts" })}
              </h3>
              <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {isPercentMode
                  ? t("weekly_recap.charts.unified_subtitle", {
                    defaultValue: "One curve per metric — 100% reference = planned values by MD+N",
                  })
                  : t("weekly_recap.charts.subtitle", {
                    defaultValue: "Planned vs actual values by metric and MD+N day",
                  })}
              </p>
            </div>

            <div className="inline-flex rounded-lg bg-gray-200 p-0.5 dark:bg-gray-800/80">
              <button
                type="button"
                onClick={() => setChartDisplayMode?.("absolute")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  !isPercentMode
                    ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {t("weekly_recap.controls.absolute", { defaultValue: "Real values" })}
              </button>

              <button
                type="button"
                onClick={() => setChartDisplayMode?.("percent")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  isPercentMode
                    ? "bg-white text-cyan-600 shadow-sm dark:bg-gray-700 dark:text-cyan-300"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {t("weekly_recap.controls.percentages", { defaultValue: "Percentage (%)" })}
              </button>
            </div>
          </div>

          {isPercentMode ? (
            <UnifiedMetricsPercentChart
              metrics={metrics}
              jDays={visibleDays}
              plannedValues={plannedValues}
              actualValues={actualValues}
              metricLabelOverrides={metricLabelOverrides}
              dayLabelOverrides={dayLabelOverrides}
              isDarkMode={isDarkMode}
              t={t}
            />
          ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {metrics.map((metric) => {
              const chartRows = visibleDays.map((day) => {
                const rawPlanned = toNullableNumber(plannedValues?.[metric.key]?.[day]);
                const rawActual = toNullableNumber(actualValues?.[metric.key]?.[day]);

                return {
                  day,
                  planned: rawPlanned,
                  actual: rawActual,
                  rawPlanned,
                  rawActual,
                };
              });

              return (
                <div
                  key={`${player?.id}-${metric.key}-chart`}
                  className={`rounded-xl border p-3 ${
                    isDarkMode ? "border-gray-700 bg-gray-800/70" : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <h4 className="text-sm font-semibold">{getMetricDisplayLabel(metric, t, metricLabelOverrides)}</h4>
                    <SimilarityBadge value={filteredSimilarityByMetric?.[metric.key]} isDarkMode={isDarkMode} />
                  </div>

                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows} barCategoryGap={14}>
                        <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? "#374151" : "#d1d5db"} />
                        <XAxis dataKey="day" stroke={isDarkMode ? "#9ca3af" : "#6b7280"} tick={{ fontSize: 12 }} tickFormatter={(day) => getDayDisplayLabel(day, dayLabelOverrides)} />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                          cursor={{
                            fill: isDarkMode
                              ? "rgba(148, 163, 184, 0.12)"
                              : "rgba(15, 23, 42, 0.06)",
                          }}
                          content={({ active, payload, label }) => (
                            <WeeklyChartTooltip
                              active={active}
                              payload={payload}
                              label={label}
                              metric={metric}
                              isDarkMode={isDarkMode}
                              t={t}
                              jDays={visibleDays}
                              isPercentMode={false}
                            />
                          )}
                        />

                        <Bar
                          dataKey="planned"
                          name={t("weekly_recap.series.planned", { defaultValue: "Planned" })}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartRows.map((entry, index) => (
                            <Cell
                              key={`planned-${metric.key}-${entry.day}`}
                              fill={DAY_COLOR_PALETTE[index % DAY_COLOR_PALETTE.length]}
                              fillOpacity={0.34}
                            />
                          ))}
                        </Bar>

                        <Bar
                          dataKey="actual"
                          name={t("weekly_recap.series.actual", { defaultValue: "Actual" })}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartRows.map((entry, index) => (
                            <Cell
                              key={`actual-${metric.key}-${entry.day}`}
                              fill={DAY_COLOR_PALETTE[index % DAY_COLOR_PALETTE.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      ) : null}

      {isInternalMode ? (
        <div className={`rounded-2xl ring-1 p-4 ${isDarkMode ? "bg-gray-900/70 ring-white/10" : "bg-white ring-black/10"}`}>
          <div className="mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide">
              {t("weekly_recap.charts.internal_title", {
                defaultValue: "Internal reference comparison",
              })}
            </h3>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("weekly_recap.charts.internal_subtitle", {
                defaultValue: "Actual internal values compared with player references.",
              })}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {metrics.map((metric) => {
              const reference = toNullableNumber(referenceValues?.[metric.key]);

              const chartRows = jDays.map((day) => {
                const rawActual = toNullableNumber(actualValues?.[metric.key]?.[day]);

                return {
                  day,
                  planned: reference,
                  actual: rawActual,
                  rawPlanned: reference,
                  rawActual,
                };
              });

              return (
                <div
                  key={`${player?.id}-${metric.key}-internal-chart`}
                  className={`rounded-xl border p-3 ${
                    isDarkMode ? "border-gray-700 bg-gray-800/70" : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <h4 className="text-sm font-semibold">{getMetricDisplayLabel(metric, t, metricLabelOverrides)}</h4>
                  </div>

                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows} barCategoryGap={14}>
                        <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? "#374151" : "#d1d5db"} />
                        <XAxis dataKey="day" stroke={isDarkMode ? "#9ca3af" : "#6b7280"} tick={{ fontSize: 12 }} tickFormatter={(day) => getDayDisplayLabel(day, dayLabelOverrides)} />
                        <YAxis stroke={isDarkMode ? "#9ca3af" : "#6b7280"} tick={{ fontSize: 12 }} />

                        <Tooltip
                          cursor={{
                            fill: isDarkMode
                              ? "rgba(148, 163, 184, 0.12)"
                              : "rgba(15, 23, 42, 0.06)",
                          }}
                          content={({ active, payload, label }) => (
                            <WeeklyChartTooltip
                              active={active}
                              payload={payload}
                              label={label}
                              metric={metric}
                              isDarkMode={isDarkMode}
                              t={t}
                              jDays={jDays}
                              isPercentMode={false}
                              plannedLabel={t("weekly_recap.series.reference", {
                                defaultValue: "Reference",
                              })}
                              actualLabel={t("weekly_recap.series.actual", {
                                defaultValue: "Actual",
                              })}
                            />
                          )}
                        />

                        <Bar
                          dataKey="planned"
                          name={t("weekly_recap.series.reference", { defaultValue: "Reference" })}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartRows.map((entry, index) => (
                            <Cell
                              key={`reference-${metric.key}-${entry.day}`}
                              fill={DAY_COLOR_PALETTE[index % DAY_COLOR_PALETTE.length]}
                              fillOpacity={0.34}
                            />
                          ))}
                        </Bar>

                        <Bar
                          dataKey="actual"
                          name={t("weekly_recap.series.actual", { defaultValue: "Actual" })}
                          radius={[8, 8, 0, 0]}
                        >
                          {chartRows.map((entry, index) => (
                            <Cell
                              key={`actual-internal-${metric.key}-${entry.day}`}
                              fill={DAY_COLOR_PALETTE[index % DAY_COLOR_PALETTE.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
